<template>
  <apex-charts
    ref="apex"
    class="c-graph-column"
    type="bar"
    :width="width"
    :height="height"
    :options="options"
    :series="series"
  />
</template>

<script>
import { getScrollParent } from '@convenia/helpers'
import ApexChartsCompat from './ApexChartsCompat.js'
import CommonOptions from './CommonOptions.js'

const getPercent = (w, n, x) => {
  const percentWidth = Math.round((x / (w / n)) * 100)
  return `${percentWidth}%`
}

export default {
  name: 'CGraphColumn',

  mixins: [ CommonOptions ],

  components: {
    ApexCharts: ApexChartsCompat
  },

  props: {
    width: {
      type: Number,
      default: undefined
    },

    height: {
      type: Number,
      default: undefined
    },

    /**
     * The width of each column in the chart.
     */
    columnWidth: {
      type: Number,
      default: undefined
    },

    /**
     * The width of the grid in the chart.
     * If not provided, it will be calculated based on the number of categories and the bar width.
     */
    strokeWidth: {
      type: Number,
      default: 0
    },

    /*
      * The position of the border radius for the columns.
      * Can be 'start' or 'end'.
      */
    borderRadiusPosition: {
      type: String,
      default: undefined,
      validator: value => [ 'start', 'end' ].includes(value)
    },

    value: {
      type: Array,
      default: () => ([])
    },

    categories: {
      type: Array,
      default: () => ([])
    },

    tooltip: {
      type: Function,
      default: ({ name, data, dataPointIndex }) => `${name}: ${data[dataPointIndex]}`
    },

    xOptions: {
      type: Object,
      default: () => ({
        title: undefined,
        min: undefined,
        max: undefined,
        height: undefined,
        tickAmount: undefined,
        formatter: undefined,
        trimLabels: undefined,
      })
    },

    yOptions: {
      type: Object,
      default: () => ({
        title: undefined,
        min: undefined,
        max: undefined,
        width: undefined,
        tickAmount: undefined,
        formatter: undefined,
        trimLabels: undefined,
      })
    },

    colors: {
      type: Array,
      default: () => ([ '#6FBAFF' ])
    },

    /**
    * Disabled hover effect on the bars.
    */
    noHover: Boolean,

    /**
    * Enable or disable pointer events on the chart
    */
    enableZoom: Boolean,

    /**
     * The filter to apply on hover.
     * If noHover is true, this will be ignored.
     */

    stacked: Boolean,
    /**
     * Custom legend definitions. Each item: { label: string, color: string }.
     * @type {Array<Object>}
     * @default []
     */
    customLegends: {
      type: Array,
      default: () => []
    },

    /**
    * Controls whether the legends are shown or not.
    */
    showLegends: {
      type: Boolean,
      default: true
    },
  },

  watch: {
    value (value) {
      this.series = value
    },
  },

  computed: {
    options () {
      return {
        chart: {
          stacked: this.stacked,
          foreColor: 'rgba(18,30,72,0.8)',
          dropShadow: {
            enabled: true,
            color: this.colors,
            opacity: 0.2,
            top: 0,
            left: 0,
            blur: 3,
          },
          animations: { enabled: false },
          toolbar: { show: false },
          zoom: { enabled: this.enableZoom }
        },
        fill: { opacity: 1.0, },
        states: {
          normal: { filter: { type: 'none' } },
          hover: { filter: this.barHover },
          active: { filter: { type: 'none' } },
        },
        grid: { borderColor: 'rgba(18,30,72,0.1)' },
        colors: this.colors,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: this.calculateColumnWitdh,
            borderRadius: 2,
            ...(this.borderRadiusPosition &&
              { borderRadiusApplication: this.borderRadiusPosition }
            ),
          }
        },
        stroke: {
          show: this.strokeWidth > 0,
          width: this.strokeWidth,
          colors: [ 'transparent' ]
        },
        legend: {
          show: this.showLegends,
          customLegendItems: this.customLegendLabels,
          position: 'bottom',
          horizontalAlign: 'left',
          showForSingleSeries: true,
          fontSize: '10px',
          fontWeight: 400,
          fontFamily: 'Ubuntu',
          markers: {
            width: 8,
            height: 8,
            radius: 8,
            fillColors: this.customLegendColors,
          },
          itemMargin: {
            vertical: 5
          },
          onItemClick: {
            toggleDataSeries: false
          }
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const { data, name } = w.globals.initialSeries[seriesIndex]
            const category = this.categories[dataPointIndex]

            const content = this.tooltip({ data, name, category, seriesIndex, dataPointIndex, w })
            const scrollParent = getScrollParent(this.$el) || {}
            const scrollTop = scrollParent.scrollTop || 0

            return `<div class="c-tooltip">
            <div class="c-popover popover -top -dark" style="transform: translate(-50%, calc(-120% - ${scrollTop}px)); --left: calc(50% - 5px);">
              <div class="card" style="--tooltip-font-size: 11px; --tooltip-max-width: 200px; --tooltip-horizontal-padding: 10px; text-align: left">
                ${content}
              </div>
            </div>
          </div>`
          }
        },
        xaxis: {
          categories: this.categories,
          min: this.xOptions.min,
          max: this.xOptions.max,
          tickPlacement: 'on',
          tickAmount: this.xOptions.tickAmount,
          axisBorder: { show: false },
          crosshairs: { show: false },
          title: {
            text: this.xOptions.title, // Define title so it "ocuppies" space
            style: { color: 'transparent' }
          },
          labels: {
            minHeight: this.xOptions.height,
            maxHeight: this.xOptions.height,
            rotate: 0,
            trim: this.xOptions.trimLabels,
            hideOverlappingLabels: false,
            formatter: this.xOptions.formatter || (value => `${value}`),
            style: {
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: 'Nunito Sans',
              colors: 'rgba(55,61,63,0.5)'
            }
          }
        },
        yaxis: {
          min: this.yOptions.min,
          max: this.yOptions.max,
          tickAmount: this.yOptions.tickAmount,
          labels: {
            minWidth: this.yOptions.width || 70,
            maxWidth: this.yOptions.width || 70,
            formatter: this.yOptions.formatter || (value => `${value}`),
            style: {
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: 'Nunito Sans',
              colors: 'rgba(55,61,63,0.5)'
            }
          }
        }
      }
    },

    barWidth () {
      return this.stacked ? 20 : 9
    },

    calculateColumnWitdh () {
      if (this.columnWidth) return this.columnWidth

      if (this.gridWidth)
        return getPercent(this.gridWidth, this.categories.length, this.barWidth)

      return 5
    },

    barHover () {
      return this.noHover ? { type: 'none' } : this.hoverFilter
    },

    customLegendLabels () {
      return this.customLegends.map(item => item?.label) || []
    },

    customLegendColors () {
      return this.customLegends.map(item => item?.color) || []
    },
  },

  data: (vm) => ({
    series: vm.value,
    gridWidth: vm.width
  }),

  mounted () {
    this.observeGridWidthChange()
    this.$nextTick(this.updateGridWidth)
  },

  beforeDestroy () {
    if (this._gridWidthObserver) this._gridWidthObserver.disconnect()
  },

  beforeUnmount () {
    if (this._gridWidthObserver) this._gridWidthObserver.disconnect()
  },

  methods: {
    updateGridWidth () {
      const el = this.$el.querySelector('.apexcharts-inner')
      if (!el) return

      const { width } = el.getBoundingClientRect()

      this.gridWidth = width
    },

    observeGridWidthChange () {
      this._gridWidthObserver = new MutationObserver(this.updateGridWidth)
      this._gridWidthObserver.observe(this.$el, { childList: true, attributeFilter: [ 'style' ] })
    }
  }
}
</script>

<style lang="scss">
.c-graph-column {
  .apexcharts-legend {
    .apexcharts-legend-marker { margin-right: 5px; }
    .apexcharts-legend-text { text-transform: uppercase; }
    .apexcharts-legend-series { display: inline-flex !important; }
  }
}
</style>
